import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CloseButton, Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { ArrowPathIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import {
  AdManagementService,
  type BrandLookupMatch,
  type CompetitorView,
  type SuggestedCompetitor,
} from '../../client'
import { scrapeLabel } from '../../lib/competitorAds'

const SEARCH_DELAY = 500
const MIN_QUERY = 2

const SECTION = 'px-3 pt-2 pb-1 text-[10px] font-medium tracking-wide text-slate-500 uppercase'
const ROW = 'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition'

type Props = {
  brandId: number
  competitors: CompetitorView[]
  suggested: SuggestedCompetitor[]
  activePageIds: Set<string>
  busy: boolean
  onToggle: (pageId: string, active: boolean) => void
  onAdd: (competitor: BrandLookupMatch | SuggestedCompetitor) => void
  onRefresh: (pageId: string) => void
  refreshing: Set<string>
}

function Avatar({ src, name }: { src?: string | null; name: string }) {
  if (src) {
    return <img src={src} alt="" referrerPolicy="no-referrer" className="size-7 shrink-0 rounded-full object-cover" />
  }

  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] text-slate-500">
      {(name[0] ?? '?').toUpperCase()}
    </span>
  )
}

export default function CompetitorsSelector({
  brandId,
  competitors,
  suggested,
  activePageIds,
  busy,
  onToggle,
  onAdd,
  onRefresh,
  refreshing,
}: Props) {
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<BrandLookupMatch[]>([])
  const [looking, setLooking] = useState(false)
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => clearTimeout(debounce.current ?? undefined), [])

  const trimmed = query.trim()
  const tracked = competitors.filter(({ name }) => name.toLowerCase().includes(trimmed.toLowerCase()))
  const selected = competitors.filter(({ page_id }) => activePageIds.has(page_id))
  const untracked = suggested.filter(({ page_id }) => !competitors.some((c) => c.page_id === page_id))

  const extra = selected.length - 2
  const label = selected.length
    ? `${selected.slice(0, 2).map(({ name }) => name).join(', ')}${extra > 0 ? ` +${extra}` : ''}`
    : 'Ad Accounts'

  function search(next: string) {
    setQuery(next)
    clearTimeout(debounce.current ?? undefined)

    if (next.trim().length < MIN_QUERY) {
      setMatches([])
      setLooking(false)
      return
    }

    setLooking(true)
    debounce.current = setTimeout(async () => {
      try {
        const { matches: found } = await AdManagementService.lookupBrand(brandId, { query: next.trim() })
        setMatches(found ?? [])
      } finally {
        setLooking(false)
      }
    }, SEARCH_DELAY)
  }

  return (
    <Popover className="relative">
      <PopoverButton className="inline-flex max-w-xs items-center gap-2 rounded-lg border border-gray-300 bg-stone-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-stone-200">
        {selected.length > 0 && (
          <span className="flex shrink-0 -space-x-2">
            {selected.slice(0, 3).map(({ page_id, profile_picture_url, name }) => (
              <img
                key={page_id}
                src={profile_picture_url ?? ''}
                alt={name}
                referrerPolicy="no-referrer"
                className="size-5 rounded-full border border-stone-100 object-cover"
              />
            ))}
          </span>
        )}
        <span className="truncate">{label}</span>
        <ChevronDownIcon className="size-3.5 shrink-0 text-gray-400" />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: 'bottom end', gap: 8 }}
        onMouseLeave={() => setTooltip(null)}
        className="z-50 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
      >
        <div className="flex items-center border-b border-slate-100 px-3 py-2">
          <MagnifyingGlassIcon className="size-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(event) => search(event.target.value)}
            placeholder="Search Ad Accounts"
            className="ml-2 flex-1 bg-transparent text-sm placeholder-slate-400 focus:outline-none"
          />
        </div>

        <div className="max-h-[26rem] overflow-y-auto py-1 [scrollbar-width:thin]">
          {tracked.length > 0 && (
            <>
              <p className={SECTION}>Tracked</p>
              <ul className="px-1">
                {tracked.map((competitor) => {
                  const active = activePageIds.has(competitor.page_id)
                  const spinning = refreshing.has(competitor.page_id)
                  return (
                    <li key={competitor.page_id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggle(competitor.page_id, !active)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onToggle(competitor.page_id, !active)
                          }
                        }}
                        className={`${ROW} cursor-pointer ${
                          active ? 'bg-purple-100 hover:bg-purple-200' : 'hover:bg-slate-50'
                        }`}
                      >
                        <Avatar src={competitor.profile_picture_url} name={competitor.name} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span
                            className={`truncate text-xs font-medium ${active ? 'text-purple-900' : 'text-slate-800'}`}
                          >
                            {competitor.name}
                          </span>
                          <span className="truncate text-[11px] text-slate-500">{scrapeLabel(competitor)}</span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Refresh ${competitor.name}`}
                          disabled={busy || spinning}
                          onMouseEnter={(event) => {
                            const { left, top, width } = event.currentTarget.getBoundingClientRect()
                            setTooltip({
                              text: busy || spinning ? 'Update in progress' : 'Refresh',
                              x: left + width / 2,
                              y: top,
                            })
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={(event) => {
                            event.stopPropagation()
                            onRefresh(competitor.page_id)
                          }}
                          className="shrink-0 rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
                        >
                          <ArrowPathIcon className={`size-4 ${spinning || busy ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          )}

          {!trimmed && untracked.length > 0 && (
            <>
              <p className={SECTION}>Suggested</p>
              <ul className="px-1">
                {untracked.map((competitor) => (
                  <li key={competitor.page_id}>
                    <CloseButton
                      disabled={busy}
                      onClick={() => onAdd(competitor)}
                      className={`${ROW} hover:bg-slate-50 disabled:opacity-50`}
                    >
                      <Avatar src={competitor.profile_picture_url} name={competitor.name} />
                      <span className="flex-1 truncate text-xs font-medium text-slate-800">{competitor.name}</span>
                    </CloseButton>
                  </li>
                ))}
              </ul>
            </>
          )}

          {trimmed && (
            <>
              <p className={SECTION}>Search results</p>
              {looking ? (
                <div className="flex items-center justify-center gap-2 p-3 text-xs text-slate-500">
                  <span className="inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-purple-500" />
                  Searching, this might take a moment…
                </div>
              ) : matches.length > 0 ? (
                <ul className="px-1 pb-1">
                  {matches.map((match) => {
                    const added = competitors.some(({ page_id }) => page_id === match.page_id)
                    return (
                      <li key={match.page_id}>
                        <CloseButton
                          disabled={added || busy}
                          onClick={() => onAdd(match)}
                          className={`${ROW} hover:bg-slate-50 disabled:opacity-50`}
                        >
                          <Avatar src={match.profile_picture_url} name={match.name} />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-xs font-medium text-slate-800">{match.name}</span>
                            <span className="truncate text-[11px] text-slate-500">
                              {[match.category, match.likes && `${match.likes.toLocaleString()} likes`]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </span>
                          {added && <span className="shrink-0 text-[10px] font-medium text-purple-700">Added</span>}
                        </CloseButton>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="px-3 py-2 text-xs text-slate-500">No matches for "{trimmed}".</p>
              )}
            </>
          )}

          {!tracked.length && !trimmed && !untracked.length && (
            <p className="p-3 text-xs text-slate-500">Search to add an Ad Account.</p>
          )}
        </div>
      </PopoverPanel>

      {tooltip &&
        createPortal(
          <span
            style={{ left: tooltip.x, top: tooltip.y }}
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-[calc(100%+6px)] rounded-md bg-stone-900 px-2 py-1 text-[11px] whitespace-nowrap text-white shadow-lg"
          >
            {tooltip.text}
          </span>,
          document.body,
        )}
    </Popover>
  )
}
